export interface ThumbnailToken {
  sig: string;
  exp: number;
  expiresAt: string; // ISO UTC
  scope: string;
  baseUrl: string;
}

// eslint-disable-next-line no-unused-vars
type Fetcher = () => Promise<ThumbnailToken>;
type Listener = () => void;

const SKEW_MS = 10 * 60 * 1000; // refresh 10 min before actual expiry

let fetcher: Fetcher | null = null;
let token: ThumbnailToken | null = null;
let refreshPromise: Promise<ThumbnailToken> | null = null;
const listeners = new Set<Listener>();

/** Call once at app bootstrap with the app's authenticated API client. */
export function configureThumbnailToken(f: Fetcher): void {
  fetcher = f;
}

/** Call on logout so the next login fetches a token for the new user/customer. */
export function clearThumbnailToken(): void {
  token = null;
  refreshPromise = null;
  emit();
}

export function getSnapshot(): ThumbnailToken | null {
  return token;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(): void {
  listeners.forEach((l) => l());
}

function isFresh(t: ThumbnailToken | null): t is ThumbnailToken {
  return !!t && Date.parse(t.expiresAt) - SKEW_MS > Date.now();
}

/**
 * Returns a valid token, fetching one if missing or stale.
 * Stampede guard: concurrent callers (a grid mounting hundreds of <Thumbnail>)
 * share ONE in-flight request via refreshPromise.
 */
export function getValidToken(): Promise<ThumbnailToken> {
  if (isFresh(token)) return Promise.resolve(token);
  return forceRefresh();
}

/** Unconditional refresh (reactive path after an image 401). Also deduped. */
export function forceRefresh(): Promise<ThumbnailToken> {
  if (refreshPromise) return refreshPromise;
  if (!fetcher) return Promise.reject(new Error('thumbnail token fetcher not configured'));

  refreshPromise = fetcher()
    .then((t) => {
      token = t;
      emit();
      return t;
    })
    .finally(() => {
      refreshPromise = null; // success or failure: next caller may try again
    });

  return refreshPromise;
}
